import React from 'react';
import { School, Users, GraduationCap, Heart, BookOpen, Activity, TrendingUp, AlertTriangle, AlertOctagon, XCircle, Eye, Info, CheckCircle2, Clock, Server, RefreshCw, Tag } from 'lucide-react';
import { motion } from 'framer-motion';
import { PageLayout } from '../../shared/PageLayout';
import { StatCard } from '../../shared/StatCard';
import { ConnectionBadge } from '../shared/ConnectionBadge';
import { QualityRing, ProgressBar } from './dashboard/DashboardCharts';
import { containerVariants, itemVariants } from '../shared/animationVariants';
import { useDashboardData } from './dashboard/useDashboardData';
export function DashboardPage({
  onNavigate
}: {
  onNavigate?: (view: string) => void;
}) {
  const {
    stats,
    contentStats,
    pipelineLoading,
    strictModeOn,
    handleRefreshPipeline,
    passCount,
    warnCount,
    failCount,
    avgScore,
    totalValidated,
    severityCounts,
    systemHealth,
    recentActivity
  } = useDashboardData();
  return <PageLayout title="Dashboard" subtitle="Overzicht van het Meester djay platform" actions={<ConnectionBadge status="live" data-id="element-6718" data-mp="xwnjvmr733uy" />} data-id="element-6717" data-mp="t444ck8y5yni">
      {/* Section 1: Platform Overview */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6" data-id="element-6719" data-mp="k5ifk7cvtnea">
        <motion.div variants={itemVariants} data-id="element-6720" data-mp="1bv559dtmo49p">
          <StatCard label="Leerlingen" value={stats.totalLearners} icon={<Users className="w-4 h-4 text-blue-500" data-id="element-6722" data-mp="1gtyh71t9nn7r" />} data-id="element-6721" data-mp="1bwg4d0c4onuh" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-6723" data-mp="12ugo4a8xtnsl">
          <StatCard label="Docenten" value={stats.totalTeachers} icon={<GraduationCap className="w-4 h-4 text-indigo-500" data-id="element-6725" data-mp="3nl7f6v2e824l" />} data-id="element-6724" data-mp="17u1q6tjemc9j" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-6726" data-mp="3skaiob2q60ag">
          <StatCard label="Ouders" value={stats.totalParents} icon={<Heart className="w-4 h-4 text-rose-500" data-id="element-6728" data-mp="1y30rn24agimt" />} data-id="element-6727" data-mp="3sl244t2lyhn0" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-6729" data-mp="1t4pfgkqitw1f">
          <StatCard label="Scholen" value={stats.totalSchools} icon={<School className="w-4 h-4 text-purple-500" data-id="element-6731" data-mp="1ocfw5njwvu84" />} data-id="element-6730" data-mp="1t5r11j55y0xe" />
        </motion.div>
      </motion.div>

      {/* Section 2: Content & Quality */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5 mb-6" data-id="element-6732" data-mp="1fe3wyx4zbo42">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4" data-id="element-6733" data-mp="15ev7agh5cvni">
          Content & Kwaliteit
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-6734" data-mp="11fdzhge8zmf6">
          {/* Left: Content Stats */}
          <div className="space-y-4" data-id="element-6735" data-mp="2zsw5cz4vhemj">
            <div className="flex items-center justify-between" data-id="element-6736" data-mp="2vrua0te2tmpu">
              <div className="flex items-center gap-2" data-id="element-6737" data-mp="2vslvvchvg62x">
                <BookOpen className="w-4 h-4 text-blue-500" data-id="element-6738" data-mp="hkutqvoy17h9" />
                <span className="text-sm font-medium text-gray-700" data-id="element-6739" data-mp="hi8k8qihshwe">
                  Vragenbank
                </span>
              </div>
              <span className="text-lg font-bold text-gray-900" data-id="element-6740" data-mp="r9mgmpf5d1lb">
                {contentStats.totalQuestions.toLocaleString('nl')}
              </span>
            </div>
            <div className="flex items-center justify-between" data-id="element-6741" data-mp="3uql6jg59rk7p">
              <div className="flex items-center gap-2" data-id="element-6742" data-mp="3usfqos8pv8aq">
                <Tag className="w-4 h-4 text-purple-500" data-id="element-6743" data-mp="3jid0i1yl1ub" />
                <span className="text-sm font-medium text-gray-700" data-id="element-6744" data-mp="3m4w9ey7p5sd">
                  Families
                </span>
              </div>
              <span className="text-sm text-gray-600" data-id="element-6745" data-mp="cl8fqk1ajn7e">
                {contentStats.totalFamilies} / {contentStats.taxonomyFamilies}
              </span>
            </div>
            <ProgressBar value={contentStats.coveragePct} color={contentStats.coveragePct >= 80 ? 'bg-emerald-500' : contentStats.coveragePct >= 50 ? 'bg-amber-500' : 'bg-red-500'} label="Seed dekking" data-id="element-6746" data-mp="1o056pzh1cmhe" />
            <ProgressBar value={contentStats.visualCoveragePct} color={contentStats.visualCoveragePct >= 80 ? 'bg-emerald-500' : contentStats.visualCoveragePct >= 50 ? 'bg-amber-500' : 'bg-red-500'} label={`Visuele componenten (${contentStats.usedVisualsCount}/${contentStats.registeredVisuals})`} data-id="element-6747" data-mp="1vkzb1jfrq64u" />
          </div>

          {/* Right: Quality & Validation */}
          <div className="space-y-4" data-id="element-6748" data-mp="2aawtdir0o1m4">
            <div className="flex items-center justify-between" data-id="element-6749" data-mp="2o7k3kn7zco9z">
              <div className="flex items-center gap-3" data-id="element-6750" data-mp="2j98s6821e5pn">
                {avgScore !== null ? <QualityRing score={avgScore} data-id="element-6751" data-mp="32v0vxs4g2l2y" /> : <div className="w-12 h-12 rounded-full bg-gray-100 animate-pulse" data-id="element-6752" data-mp="32vtsy1dlpw7w" />}
                <div data-id="element-6753" data-mp="37tc3txb6ilij">
                  <div className="text-sm font-medium text-gray-700" data-id="element-6754" data-mp="2tuchiwb52ipj">
                    Kwaliteitsscore
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-6755" data-mp="2xv65tqshtziy">
                    alle {contentStats.totalQuestions.toLocaleString('nl')}{' '}
                    vragen
                  </div>
                </div>
              </div>
              <button onClick={handleRefreshPipeline} disabled={pipelineLoading} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-40" title="Validatie vernieuwen" data-id="element-6756" data-mp="2owu5uy5qlpdu">
                <RefreshCw className={`w-4 h-4 ${pipelineLoading ? 'animate-spin' : ''}`} data-id="element-6757" data-mp="216glltte8utt" />
              </button>
            </div>

            {pipelineLoading ? <div className="flex gap-4" data-id="element-6758" data-mp="bpuj8zvprhx8">
                {['Geslaagd', 'Review', 'Afgekeurd'].map(l => <div key={l} className="flex-1 bg-gray-50 rounded-lg p-2 animate-pulse" data-id="element-6759" data-mp="6vqdul23jhy9">
                    <div className="h-5 bg-gray-200 rounded mb-1" data-id="element-6760" data-mp="3u3k8y0zf9e9r" />
                    <div className="h-3 bg-gray-100 rounded w-2/3" data-id="element-6761" data-mp="un5gyg1p63ak" />
                  </div>)}
              </div> : passCount !== null ? <>
                <div className="flex gap-3" data-id="element-6762" data-mp="pmhorr1qfger">
                  <div className="flex-1 bg-emerald-50 rounded-lg p-2.5 text-center" data-id="element-6763" data-mp="llo559vbonro">
                    <div className="text-lg font-bold text-emerald-700" data-id="element-6764" data-mp="gofk91isi5mq">
                      {passCount}
                    </div>
                    <div className="text-xs text-emerald-600" data-id="element-6765" data-mp="36ej6cud9sxce">Geslaagd</div>
                  </div>
                  <div className="flex-1 bg-amber-50 rounded-lg p-2.5 text-center" data-id="element-6766" data-mp="31cadrz4b7xat">
                    <div className="text-lg font-bold text-amber-700" data-id="element-6767" data-mp="1gx43hh785r64">
                      {warnCount}
                    </div>
                    <div className="text-xs text-amber-600" data-id="element-6768" data-mp="1lst8w15xe2ac">Review</div>
                  </div>
                  <div className="flex-1 bg-red-50 rounded-lg p-2.5 text-center" data-id="element-6769" data-mp="1puegb6zoxod5">
                    <div className="text-lg font-bold text-red-700" data-id="element-6770" data-mp="1uosdnexwy5kg">
                      {failCount}
                    </div>
                    <div className="text-xs text-red-600" data-id="element-6771" data-mp="1zooifgefq112">Afgekeurd</div>
                  </div>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden flex" data-id="element-6772" data-mp="29krlcoh423p2">
                  <div className="h-full bg-emerald-500" style={{
                width: `${totalValidated > 0 ? (passCount ?? 0) / totalValidated * 100 : 0}%`
              }} data-id="element-6773" data-mp="29infw23kcwmo" />
                  <div className="h-full bg-amber-400" style={{
                width: `${totalValidated > 0 ? (warnCount ?? 0) / totalValidated * 100 : 0}%`
              }} data-id="element-6774" data-mp="j8tzwxrsivcw" />
                  <div className="h-full bg-red-500" style={{
                width: `${totalValidated > 0 ? (failCount ?? 0) / totalValidated * 100 : 0}%`
              }} data-id="element-6775" data-mp="9445re2movj8" />
                </div>
              </> : <div className="text-sm text-gray-400 text-center py-4" data-id="element-6776" data-mp="38j8za8pl8wb9">
                Validatie wordt geladen...
              </div>}
          </div>
        </div>
      </motion.div>

      {/* Section 2b — Phase 1: 5-level severity breakdown across ALL issues.
                  The 3 tiles above count QUESTIONS (verdict per question, always
                  3 outcomes). These 5 tiles count ISSUES by display-severity. */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5 mb-6" data-id="element-6777" data-mp="wsoil0wjb1u7">
        <div className="flex items-center justify-between mb-3" data-id="element-6778" data-mp="19dd9ic80eoww">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-6779" data-mp="vcsu7q1azb85">
            Bevindingen per ernst
          </h3>
          <span className="text-[10px] text-gray-400" data-id="element-6780" data-mp="10cf53stnsfds">
            5-niveau model · per bevinding
          </span>
        </div>
        {severityCounts === null ? <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-id="element-6781" data-mp="1t0t52e4b5lrz">
            {Array.from({
          length: 5
        }).map((_, i) => <div key={i} className="h-12 bg-gray-50 rounded-lg animate-pulse" data-id="element-6782" data-mp="1eb53rcw4mn49" data-mpi={i} />)}
          </div> : <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-id="element-6783" data-mp="26z17v2o0zhb2">
            {[{
          key: 'critical' as const,
          label: 'Kritiek',
          Icon: AlertOctagon,
          bg: 'bg-red-50',
          border: 'border-red-200',
          text: 'text-red-700',
          iconClass: 'text-red-600',
          hint: 'Blocker · ernst ≥90 (student-facing risico)'
        }, {
          key: 'blocker' as const,
          label: 'Blocker',
          Icon: XCircle,
          bg: 'bg-orange-50',
          border: 'border-orange-200',
          text: 'text-orange-700',
          iconClass: 'text-orange-600',
          hint: 'Blocker · ernst 70-89'
        }, {
          key: 'review' as const,
          label: 'Review',
          Icon: Eye,
          bg: 'bg-yellow-50',
          border: 'border-yellow-200',
          text: 'text-yellow-700',
          iconClass: 'text-yellow-600',
          hint: 'Coach · ernst ≥45 (twijfel, vraagt menselijk oordeel)'
        }, {
          key: 'coach' as const,
          label: 'Coach',
          Icon: AlertTriangle,
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          text: 'text-blue-700',
          iconClass: 'text-blue-600',
          hint: 'Coach · ernst <45 (auteursfeedback)'
        }, {
          key: 'info' as const,
          label: 'Info',
          Icon: Info,
          bg: 'bg-slate-50',
          border: 'border-slate-200',
          text: 'text-slate-700',
          iconClass: 'text-slate-500',
          hint: 'Observatie zonder actie'
        }].map(tier => {
          const count = severityCounts[tier.key] ?? 0;
          const TierIcon = tier.Icon;
          return <div key={tier.key} className={`flex items-center gap-2 p-2.5 rounded-lg border ${tier.bg} ${tier.border}`} title={tier.hint} data-id="element-6784" data-mp="9vfuuq0worek">
                  <TierIcon className={`w-4 h-4 flex-shrink-0 ${tier.iconClass}`} data-id="element-6785" data-mp="hyhwlbn2lrmz" />
                  <div className="min-w-0 flex-1" data-id="element-6786" data-mp="mxb84yisr8ki">
                    <p className={`text-lg font-bold leading-none ${tier.text}`} data-id="element-6787" data-mp="mvi1hj86ooci">
                      {count.toLocaleString('nl-NL')}
                    </p>
                    <p className={`text-[10px] font-medium ${tier.text} mt-0.5`} data-id="element-6788" data-mp="vrzg4wfrb4d0">
                      {tier.label}
                    </p>
                  </div>
                </div>;
        })}
          </div>}
        <p className="text-[10px] text-gray-400 mt-2 leading-relaxed" data-id="element-6789" data-mp="1amnhpzi1dinc">
          <span className="font-semibold" data-id="element-6790" data-mp="37gh61hzu31oa">Verdict</span> (boven) telt vragen — 3
          uitkomsten van de gate. <span className="font-semibold" data-id="element-6791" data-mp="3cfjy81uptdeu">Ernst</span>{' '}
          (hier) telt individuele bevindingen — één vraag kan meerdere
          bevindingen op verschillende niveaus hebben.
        </p>
      </motion.div>

      {/* Section 3: Activity & System Health */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6" data-id="element-6792" data-mp="1kque7o4z7quf">
        {/* Recent Activity — F-A3 (19 May 2026): timestamps in this list
            are synthetic (computed as `now - fixed offset`). Counts and
            messages ARE real, but the times are not — show a mock badge
            so admins don't read this as an audit-grade activity log.
            Audit §11 / §19. */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-6793" data-mp="1yj0iocyro9e1">
          <div className="flex items-center justify-between mb-4" data-id="element-6794" data-mp="s3q4j5uxuocb">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2" data-id="element-6795" data-mp="n2aibah0ybsc">
              <Clock className="w-4 h-4" data-id="element-6796" data-mp="2az8e2tk2jsjv" /> Recente Activiteit
            </h3>
            <ConnectionBadge status="mock" label="Demo-tijden" data-id="element-6797" data-mp="2fr01wwbubk6b" />
          </div>
          <div className="space-y-3" data-id="element-6798" data-mp="21zlhcl2abax5">
            {recentActivity.map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-6799" data-mp="26yg2vurmzdky" data-mpi={i}>
                <div className={`mt-0.5 flex-shrink-0 ${item.type === 'success' ? 'text-emerald-500' : item.type === 'warning' ? 'text-amber-500' : 'text-blue-500'}`} data-id="element-6800" data-mp="26yofdljukgvm">
                  {item.type === 'success' ? <CheckCircle2 className="w-4 h-4" data-id="element-6801" data-mp="2kqcqblid0zb0" /> : item.type === 'warning' ? <AlertTriangle className="w-4 h-4" data-id="element-6802" data-mp="2po4txcsda9v1" /> : <Activity className="w-4 h-4" data-id="element-6803" data-mp="19axrsdai5zds" />}
                </div>
                <div className="min-w-0 flex-1" data-id="element-6804" data-mp="1e9r5o6tpbseh">
                  <p className="text-sm text-gray-700 leading-snug" data-id="element-6805" data-mp="1eakah293o7tz">
                    {item.message}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5 font-mono" data-id="element-6806" data-mp="16sckq1voe3y9">
                    {item.time}
                  </p>
                </div>
              </div>)}
          </div>
        </motion.div>

        {/* System Health */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-6807" data-mp="1arlg1b6d69vx">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-6808" data-mp="n06a78fdpafx">
            <Server className="w-4 h-4" data-id="element-6809" data-mp="ws27widjyyjm" /> Systeemgezondheid
          </h3>
          <div className="space-y-4" data-id="element-6810" data-mp="wu56vrwoo20h">
            <div className="flex items-center justify-between" data-id="element-6811" data-mp="s0twxhwkr54c">
              <span className="text-sm text-gray-600" data-id="element-6812" data-mp="rz92rte69o70">Strict Mode</span>
              <span className={`text-xs font-bold px-2 py-1 rounded-full ${strictModeOn ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-6813" data-mp="4agbwbceq6j9">
                {strictModeOn ? 'AAN' : 'UIT'}
              </span>
            </div>
            <div className="flex items-center justify-between" data-id="element-6814" data-mp="24d8w0tir3t5o">
              <span className="text-sm text-gray-600" data-id="element-6815" data-mp="24bo1v50cmc8c">Gem. responstijd</span>
              <span className="text-sm font-semibold text-gray-800" data-id="element-6816" data-mp="295isksedfrgx">
                {systemHealth.responseTime}ms
              </span>
            </div>
            <div data-id="element-6817" data-mp="1gglez2l3uiui">
              <div className="flex items-center justify-between mb-1.5" data-id="element-6818" data-mp="1gh3dy6z5jb64">
                <span className="text-sm text-gray-600" data-id="element-6819" data-mp="1lemsz93obrz4">Opslag</span>
                <span className="text-sm font-semibold text-gray-800" data-id="element-6820" data-mp="1lgzlgg725kqf">
                  {systemHealth.storageMb} / 5 MB
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-6821" data-mp="1ugmby6y9164p">
                <div className="h-full bg-violet-500 rounded-full" style={{
                width: `${systemHealth.storagePct}%`
              }} data-id="element-6822" data-mp="31u67m68j1km1" />
              </div>
            </div>
            <div data-id="element-6823" data-mp="u9qzd6kfg4tb">
              <div className="flex items-center justify-between mb-1.5" data-id="element-6824" data-mp="q61d4nr85zbg">
                <span className="text-sm text-gray-600" data-id="element-6825" data-mp="q6ultu9quyw8">Actief deze week</span>
                <span className="text-sm font-semibold text-gray-800" data-id="element-6826" data-mp="le1hv8m3nul7">
                  {stats.activeThisWeek} leerlingen
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-6827" data-mp="bhoflmcho8eu">
                <div className="h-full bg-blue-500 rounded-full" style={{
                width: `${Math.min(100, stats.activeThisWeek / Math.max(stats.totalLearners, 1) * 100)}%`
              }} data-id="element-6828" data-mp="6i2179kxcm4g" />
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Section 4: Quick Insights */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4" data-id="element-6829" data-mp="1ws5b2ckv5ep7">
        {/* Subscriptions */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-6830" data-mp="15p5nin5e6o5q">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4" data-id="element-6831" data-mp="qzg9vsg44rok">
            Abonnementen
          </h3>
          <div className="space-y-2.5" data-id="element-6832" data-mp="vwzmyp12qyex">
            {stats.planDistribution.map(item => <div key={item.label} data-id="element-6833" data-mp="i1nu6n6hbi0n">
                <div className="flex justify-between text-xs mb-1" data-id="element-6834" data-mp="m1o8m7tkvt7p">
                  <span className="text-gray-600" data-id="element-6835" data-mp="lyto6gake8ea">{item.label}</span>
                  <span className="font-semibold text-gray-800" data-id="element-6836" data-mp="82y9ynr41lh8">
                    {item.count}
                  </span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-6837" data-mp="22fo3339nmqnk">
                  <div className={`h-full rounded-full ${item.color}`} style={{
                width: `${item.pct}%`
              }} data-id="element-6838" data-mp="22dlhcvfjbjon" />
                </div>
              </div>)}
          </div>
        </motion.div>

        {/* Top Learners */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-6839" data-mp="5hh3ejksetb5">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-1.5" data-id="element-6840" data-mp="oxqzpxu647x">
            <TrendingUp className="w-3.5 h-3.5" data-id="element-6841" data-mp="ejgmrij9y9h9" /> Top Leerlingen
          </h3>
          {stats.topLearners.length > 0 ? <div className="space-y-2.5" data-id="element-6842" data-mp="ogcvnpd34xmc">
              {stats.topLearners.map((item, i) => <div key={i} className="flex items-center gap-2.5" data-id="element-6843" data-mp="jh00mhc28k8o" data-mpi={i}>
                  <div className="w-6 h-6 rounded-full bg-violet-100 text-violet-700 text-xs font-bold flex items-center justify-center flex-shrink-0" data-id="element-6844" data-mp="jenm6e5igs0d">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-6845" data-mp="2lag2ybhifnjh">
                    <p className="text-xs font-medium text-gray-800 truncate" data-id="element-6846" data-mp="i6zfwdttkahu">
                      {item.name}
                    </p>
                  </div>
                  <span className="text-xs font-bold text-violet-600" data-id="element-6847" data-mp="rxuac5h7yged">
                    {item.xp.toLocaleString('nl-NL')} XP
                  </span>
                </div>)}
            </div> : <p className="text-xs text-gray-400" data-id="element-6848" data-mp="3vdrdvvkvkq3q">Geen leerlingen gevonden</p>}
        </motion.div>
      </motion.div>
    </PageLayout>;
}